import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnVisibilityState,
  createColumnHelper,
  filterFn_equalsString,
} from "@tanstack/react-table";
import { CircleSlash, Copy, ExternalLink, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { DataTable, type DataTableFeatures } from "~/components/data-table";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { CategoryRow } from "~/features/admin/categories/types";

interface CategoriesTableProps {
  rows: CategoryRow[];
  onSelect: (id: string) => void;
  emptyMessage: string;
  globalFilter: string;
  onGlobalFilterChange: (next: string) => void;
  columnFilters: ColumnFiltersState;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  columnVisibility: ColumnVisibilityState;
  onColumnVisibilityChange: (next: ColumnVisibilityState) => void;
}

const helper = createColumnHelper<DataTableFeatures, CategoryRow>();

export function CategoriesTable({
  rows,
  onSelect,
  emptyMessage,
  globalFilter,
  onGlobalFilterChange,
  columnFilters,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  columnVisibility,
  onColumnVisibilityChange,
}: CategoriesTableProps) {
  const { t } = useTranslation();

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, CategoryRow, any>[] = [
    helper.display({
      id: "deactivatedAt",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        const row = info.row.original;
        if (!row.isDeactivated) return null;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleSlash className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
            </TooltipTrigger>
            <TooltipContent>{t("ui.deactivated")}</TooltipContent>
          </Tooltip>
        );
      },
    }),
    helper.accessor("typeId", {
      header: () => t("model.category.type"),
      filterFn: filterFn_equalsString,
      enableGlobalFilter: false,
      cell: (info) => <Badge tone="default">{info.getValue()}</Badge>,
    }),
    helper.accessor("displayName", {
      header: () => t("model.common.name"),
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue()}</span>,
    }),
    helper.accessor("localeLabel", {
      header: () => t("ui.label"),
    }),
    helper.accessor("relationTypeLabel", {
      header: () => t("model.relationType.modelName"),
      enableSorting: false,
      cell: (info) => info.getValue() || "—",
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            {row.documentUrl && (
              <Button variant="outline" size="sm" asChild>
                <a href={row.documentUrl} target="_blank" rel="noreferrer">
                  <span>{t("ui.document")}</span>
                  <ExternalLink />
                </a>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleCopyId(row.id)}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  return (
    <DataTable<CategoryRow>
      columns={columns}
      data={rows}
      emptyMessage={emptyMessage}
      globalFilter={globalFilter}
      onGlobalFilterChange={onGlobalFilterChange}
      columnFilters={columnFilters}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={onColumnVisibilityChange}
    />
  );
}
